import { z } from 'zod';
import { MoneySchema } from './common.js';
// Decimal columns serialize as canonical strings; signed because outflow rows
// carry negatives in the snapshot table.
const DecimalLike = z.union([MoneySchema, z.coerce.number()]);
// ============================================================
// Category catalog — spec §31 (3 inflow + 6 outflow buckets).
// Stored as text in the DB; the UI maps by code → label/tone.
// ============================================================
export const CF_CATEGORY_CODES = [
    'AR_COL',
    'CASH_SALE',
    'OTHER_IN',
    'AP_PAY',
    'PAYROLL',
    'UTILITY',
    'TAX',
    'CAPEX',
    'LOAN',
];
export const CashflowCategoryCodeSchema = z.enum(CF_CATEGORY_CODES);
// ============================================================
// Enums (mirror Prisma)
// ============================================================
export const CashflowForecastSourceSchema = z.enum(['AR', 'AP', 'RECURRING', 'MANUAL']);
export const CashflowForecastStatusSchema = z.enum(['PROJECTED', 'ACTUAL', 'VARIANCE']);
// ============================================================
// Snapshot row — one (week × category × source) projection.
// ============================================================
export const CashflowForecastSnapshotSchema = z.object({
    id: z.uuid(),
    companyId: z.uuid(),
    baseDate: z.coerce.date(),
    weekStartDate: z.coerce.date(),
    weekEndDate: z.coerce.date(),
    weekNumber: z.number().int().positive(),
    category: z.string().min(1),
    projectedAmount: DecimalLike, // signed
    source: CashflowForecastSourceSchema,
    sourceId: z.uuid().nullable().optional(),
    description: z.string(),
    notes: z.string().nullable().optional(),
    status: CashflowForecastStatusSchema,
    createdAt: z.coerce.date(),
    updatedAt: z.coerce.date(),
});
// ============================================================
// Run header — one per run; latest per (company, baseDate) is current.
// ============================================================
export const CashflowForecastRunSchema = z.object({
    id: z.uuid(),
    companyId: z.uuid(),
    baseDate: z.coerce.date(),
    weeksAhead: z.number().int().positive(),
    openingCash: DecimalLike,
    totalProjectedInflow: DecimalLike,
    totalProjectedOutflow: DecimalLike,
    closingCash: DecimalLike,
    createdAt: z.coerce.date(),
    createdBy: z.string(),
});
// ============================================================
// Aggregated forecast view — one matrix row per category × N weeks,
// plus weekly summary + run header. This is what the page consumes.
// ============================================================
export const CashflowWeekSummarySchema = z.object({
    weekNumber: z.number().int().positive(),
    weekStartDate: z.coerce.date(),
    weekEndDate: z.coerce.date(),
    inflow: DecimalLike, // unsigned positive
    outflow: DecimalLike, // unsigned positive
    net: DecimalLike, // signed
    openBalance: DecimalLike,
    closeBalance: DecimalLike,
});
export const CashflowCategoryRowSchema = z.object({
    category: CashflowCategoryCodeSchema,
    // One amount per week in display order; signed (negative for outflow buckets).
    amounts: z.array(DecimalLike),
    total: DecimalLike,
});
export const CashflowForecastResponseSchema = z.object({
    baseDate: z.coerce.date(),
    weeksAhead: z.number().int().positive(),
    openingCash: DecimalLike,
    totalProjectedInflow: DecimalLike,
    totalProjectedOutflow: DecimalLike,
    closingCash: DecimalLike,
    // Min cash week (alert) — week with the lowest closeBalance.
    minClosingCash: DecimalLike,
    minClosingCashWeek: z.number().int().positive(),
    weeks: z.array(CashflowWeekSummarySchema),
    rows: z.array(CashflowCategoryRowSchema),
    // Raw snapshot rows in case the UI wants drill-down detail.
    snapshots: z.array(CashflowForecastSnapshotSchema),
    // Latest run header for this baseDate (null if no run yet).
    latestRun: CashflowForecastRunSchema.nullable(),
});
// ============================================================
// POST /cashflow-forecast/run
// ============================================================
export const RunCashflowForecastSchema = z.object({
    // ISO date; defaults server-side to "today".
    baseDate: z.iso.date().optional(),
    weeksAhead: z.number().int().min(1).max(52).optional(),
});
// ============================================================
// POST /cashflow-forecast/manual
// ============================================================
export const CreateManualForecastEntrySchema = z.object({
    baseDate: z.iso.date(),
    weekStartDate: z.iso.date(),
    category: CashflowCategoryCodeSchema,
    // Signed: caller passes negative for outflow categories. Service double-checks
    // sign vs category.
    amount: z.coerce.number(),
    description: z.string().min(1),
    notes: z.string().optional(),
});
// ============================================================
// Scenario view — same shape as base forecast but with totals re-scaled by
// revMultiplier (inflow categories) / expMultiplier (outflow categories).
// 'MANUAL' source rows AND OTHER_IN are treated as fixed and not scaled.
// ============================================================
export const CashflowScenarioLabelSchema = z.enum(['BASE', 'BEST', 'WORST', 'CUSTOM']);
export const CashflowScenarioResponseSchema = CashflowForecastResponseSchema.extend({
    scenarioLabel: CashflowScenarioLabelSchema,
    revMultiplier: z.number(),
    expMultiplier: z.number(),
});
// ============================================================
// Variance — past N weeks, forecast (the closest run available at the time)
// vs actual (sum of cash-account journal lines), per week.
// ============================================================
export const CashflowVarianceWeekSchema = z.object({
    weekStartDate: z.coerce.date(),
    weekEndDate: z.coerce.date(),
    weekLabel: z.string(), // e.g. "W22 (2026-05-25)"
    forecast: z.object({
        totalInflow: DecimalLike,
        totalOutflow: DecimalLike,
        net: DecimalLike,
        runId: z.uuid().nullable(),
    }),
    actual: z.object({
        totalInflow: DecimalLike,
        totalOutflow: DecimalLike,
        net: DecimalLike,
    }),
    variance: z.object({
        inflow: DecimalLike, // actual.inflow - forecast.inflow (positive = under-forecast inflow → upside)
        outflow: DecimalLike, // actual.outflow - forecast.outflow
        net: DecimalLike,
        hitRate: z.number(), // 0..100% accuracy; 100 = perfect
    }),
});
export const CashflowVarianceResponseSchema = z.object({
    endDate: z.coerce.date(),
    lookbackWeeks: z.number().int().positive(),
    weeks: z.array(CashflowVarianceWeekSchema),
    summary: z.object({
        avgHitRate: z.number(),
        bestWeek: z.string(), // weekLabel of the most-accurate week (empty string if no weeks)
        worstWeek: z.string(),
    }),
});
//# sourceMappingURL=cashflow-forecast.js.map